import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ShieldCheck } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useOrderProtectedUnits, type SafewareClaim, type SafewareProtectedUnit } from '@/api/safeware'
import { formatDateOnly, parseUTCDate } from '@/lib/utils'
import { SafewareClaimSheet } from './claim-sheet'

const UNIT_STATUS: Record<string, string> = {
  awaiting_serial: 'Needs serial',
  registering: 'Registering',
  active: 'Active',
  cancelling: 'Cancelling',
  cancelled: 'Cancelled',
  voided: 'Voided',
}

/**
 * The order's equipment covered by Safeware plans (USA-1165): each unit's policy, and its claims with
 * Safeware's latest status. Active policies can file a claim from here.
 */
export function OrderProtection({ orderUuid }: { orderUuid: string }) {
  const { data: units } = useOrderProtectedUnits(orderUuid)
  const [filing, setFiling] = useState<SafewareProtectedUnit | null>(null)

  if (!units?.length) return null

  return (
    <section className="space-y-3" aria-labelledby="protection-plans-heading">
      <h3 id="protection-plans-heading" className="text-base font-semibold">
        Protection plans
      </h3>
      <ul className="divide-y rounded-lg border bg-card">
        {units.map((unit) => (
          <li key={unit.uuid} className="space-y-3 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-3">
                <ShieldCheck
                  className={
                    unit.status === 'active'
                      ? 'mt-0.5 h-4 w-4 shrink-0 text-green-600'
                      : 'mt-0.5 h-4 w-4 shrink-0 text-muted-foreground'
                  }
                  aria-hidden="true"
                />
                <div className="min-w-0 space-y-0.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium">
                      {[unit.equipment_make, unit.equipment_model].filter(Boolean).join(' ') ||
                        unit.equipment_description}
                    </p>
                    <Badge variant={unit.status === 'active' ? 'default' : 'outline'}>
                      {UNIT_STATUS[unit.status] ?? unit.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {[
                      unit.serial_number ? `Serial ${unit.serial_number}` : null,
                      unit.policy_number ? `Policy ${unit.policy_number}` : null,
                      `Covered through ${formatDateOnly(unit.covered_through)}`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    {unit.equipment_po_uuid && unit.equipment_po_number && (
                      <>
                        {' · '}
                        <Link
                          to="/purchase-orders/$poId"
                          params={{ poId: unit.equipment_po_uuid }}
                          className="text-primary hover:underline"
                        >
                          {unit.equipment_po_number}
                        </Link>
                      </>
                    )}
                  </p>
                </div>
              </div>
              {unit.can_file_claim && (
                <Button size="sm" variant="outline" onClick={() => setFiling(unit)}>
                  File Safeware claim
                </Button>
              )}
            </div>
            {unit.claims.length > 0 && (
              <ul className="space-y-2 sm:pl-7">
                {unit.claims.map((claim) => (
                  <ClaimRow key={claim.uuid} claim={claim} />
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
      {filing && (
        <SafewareClaimSheet orderUuid={orderUuid} unit={filing} onClose={() => setFiling(null)} />
      )}
    </section>
  )
}

function ClaimRow({ claim }: { claim: SafewareClaim }) {
  const open = !claim.closed_at
  return (
    <li className="rounded-md bg-muted/40 px-3 py-2.5 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <p className="font-medium">
          Claim <span className="font-mono text-xs tabular-nums">{claim.claim_number}</span>
          <span className="font-normal text-muted-foreground"> · {claim.type_of_event_label}</span>
        </p>
        <Badge variant={open ? 'secondary' : 'outline'}>{claim.status ?? 'Filed'}</Badge>
      </div>
      {claim.sub_status && <p className="mt-1">{claim.sub_status}</p>}
      <p className="mt-1 text-xs text-muted-foreground">
        {[
          `Filed ${parseUTCDate(claim.filed_at).toLocaleDateString()}`,
          claim.last_activity ? `Last activity ${formatDateOnly(claim.last_activity)}` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        {claim.examiner_email && (
          <>
            {' · Examiner '}
            {/* eslint-disable-next-line no-restricted-syntax -- mailto link */}
            <a href={`mailto:${claim.examiner_email}`} className="text-primary hover:underline">
              {claim.examiner_email}
            </a>
            {claim.examiner_ext ? ` ext. ${claim.examiner_ext}` : ''}
          </>
        )}
      </p>
      {claim.last_error && <p className="mt-1 text-xs text-destructive">{claim.last_error}</p>}
    </li>
  )
}
